/**
 * `CompetenceInfoPanel` — pannello informativo di una competenza, condiviso tra
 * la pagina di dettaglio (`CompetenceDetail`) e il wizard di autovalutazione
 * (`AssessmentWizard`).
 *
 * Rende:
 *  - una card "hero" (stessa resa della "competenza principale" della
 *    Dashboard): badge, donut in alto a destra, nome grande, descrizione e
 *    mission incluse, e — opzionalmente — una barra metrica e uno slot azioni;
 *  - sotto, 5 card affiancate per i Livelli di proficiency e-CF (e-1..e-5) con
 *    le descrizioni specifiche del catalogo; i livelli senza testo mostrano un
 *    placeholder.
 *
 * È puramente presentazionale: riceve i dati della competenza e i valori già
 * calcolati (donut/barra). Centralizza il layout così le due pagine restano
 * coerenti.
 */
import {
  LIVELLO_LABELS,
  livelloRank,
  type CompetenceDto,
  type Livello,
} from '../shared/dto';
import { scoreColor } from './StarRating';
import { Card, Icon } from './ui';

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Anello di progresso di una percentuale `0..100` con valore al centro. */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/** Barra di progresso etichettata `0..100` (o `null` → trattino). */
function MetricBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) {
    return (
      <div className="competency-match-card__metric">
        <dt>{label}</dt>
        <dd>
          <span
            className="competency-match-card__value competency-match-card__value--empty"
            aria-label={`${label} non disponibile`}
          >
            —
          </span>
        </dd>
      </div>
    );
  }
  const clamped = Math.min(100, Math.max(0, value));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(clamped / 10);
  return (
    <div className="competency-match-card__metric">
      <dt>{label}</dt>
      <dd>
        <span
          role="progressbar"
          aria-valuenow={exact}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${formatPercent(clamped)}`}
          className="competency-match-card__bar"
        >
          <span
            className="competency-match-card__bar-fill"
            style={{ width: `${clamped}%`, ...(color ? { background: color } : {}) }}
          />
        </span>
        <span className="competency-match-card__value">{formatPercent(clamped)}</span>
      </dd>
    </div>
  );
}

export interface CompetenceInfoPanelProps {
  /** Competenza da presentare (nome, descrizione, mission, level1..5). */
  competence: Pick<
    CompetenceDto,
    'name' | 'description' | 'mission' | 'level1' | 'level2' | 'level3' | 'level4' | 'level5'
  >;
  /** Testo del badge sopra il nome (es. "Competenza", "Competenza in valutazione"). */
  badge: string;
  /** Percentuale mostrata nel donut (0..100). Default `0`. */
  donutValue?: number;
  /** Etichetta accessibile del donut (es. "Copertura", "Completamento"). */
  donutLabel?: string;
  /**
   * Valore della barra metrica sotto la descrizione. Se `undefined` la barra non
   * viene mostrata; `null` mostra il trattino "non disponibile".
   */
  metricValue?: number | null;
  /** Etichetta della barra metrica (default "Proficiency"). */
  metricLabel?: string;
  /**
   * Livello di proficiency posseduto dall'utente sulla competenza (`e-1..e-5`).
   * Quando valorizzato, la card del Livello corrispondente viene evidenziata
   * (riquadro) e mostra una spunta. `null`/`undefined`/`e-0` ⇒ nessuna
   * evidenziazione (competenza non coperta o Livello posseduto nullo).
   */
  ownedLevel?: Livello | null;
}

/** Tutti i 5 Livelli e-CF con la relativa descrizione dal catalogo (o `null`). */
function levelEntries(
  competence: CompetenceInfoPanelProps['competence'],
): { livello: Livello; label: string; text: string | null }[] {
  const raw: { livello: Livello; text: string | undefined }[] = [
    { livello: 'e-1', text: competence.level1 },
    { livello: 'e-2', text: competence.level2 },
    { livello: 'e-3', text: competence.level3 },
    { livello: 'e-4', text: competence.level4 },
    { livello: 'e-5', text: competence.level5 },
  ];
  return raw.map((e) => ({
    livello: e.livello,
    label: LIVELLO_LABELS[e.livello],
    text: e.text?.trim() ? e.text.trim() : null,
  }));
}

export function CompetenceInfoPanel({
  competence,
  badge,
  donutValue = 0,
  donutLabel = 'Copertura',
  metricValue,
  metricLabel = 'Proficiency',
  ownedLevel,
}: CompetenceInfoPanelProps) {
  const levels = levelEntries(competence);

  // Livello posseduto grezzo da evidenziare; `e-0` (o assente) non evidenzia nulla.
  const rawOwned = ownedLevel && ownedLevel !== 'e-0' ? ownedLevel : null;
  // Livelli effettivamente previsti dalla competenza (con testo esplicativo).
  const validLevels = levels.filter((l) => l.text !== null).map((l) => l.livello);
  const maxValidRank =
    validLevels.length > 0
      ? Math.max(...validLevels.map((l) => livelloRank(l)))
      : null;
  // Cap al Livello massimo supportato: se il posseduto supera il massimo previsto
  // dalla competenza, l'evidenziazione (verde/match) si sposta su quel massimo,
  // invece di segnalare un Livello non previsto. Un posseduto che cade in un
  // "buco" interno (non previsto ma sotto il massimo) resta sul suo Livello e
  // viene marcato come non valido (X).
  const highlightLevel =
    rawOwned !== null && maxValidRank !== null && livelloRank(rawOwned) > maxValidRank
      ? validLevels.find((l) => livelloRank(l) === maxValidRank) ?? rawOwned
      : rawOwned;

  return (
    <>
      {/* Card principale (hero) con descrizione e mission incluse. */}
      <article className="competency-match-card competency-match-card--hero">
        <ScoreDonut score={donutValue} label={donutLabel} />

        <div className="competency-match-card__header">
          <span className="competency-match-card__badge">{badge}</span>
          <h2 className="competency-match-card__name">{competence.name}</h2>
        </div>
        {competence.description && (
          <p className="competency-match-card__description">{competence.description}</p>
        )}
        {competence.mission && (
          <p className="competency-match-card__mission">
            <span className="competency-match-card__mission-label">Mission</span>
            {competence.mission}
          </p>
        )}
        {metricValue !== undefined && (
          <dl className="competency-match-card__metrics">
            <MetricBar label={metricLabel} value={metricValue} />
          </dl>
        )}
      </article>

      {/* 5 card dei Livelli di proficiency e-CF (sempre tutte presenti). */}
      <div className="competence-detail__levels">
        {levels.map(({ livello, label, text }) => {
          const isOwned = highlightLevel === livello;
          // Il Livello posseduto è "valido" per la competenza solo se la sua
          // card ha un testo esplicativo; se cade su un Livello non gestito lo
          // si segnala con una X (spunta ⇒ valido, X ⇒ non previsto).
          const ownedIsValid = isOwned && text !== null;
          return (
          <Card
            key={livello}
            className={[
              'competence-detail__level-card',
              text === null ? 'competence-detail__level-card--empty' : '',
              isOwned ? 'competence-detail__level-card--owned' : '',
              isOwned && !ownedIsValid ? 'competence-detail__level-card--owned-invalid' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-current={isOwned ? 'true' : undefined}
          >
            <div className="competence-detail__level-head">
              <span className="level-chip__code">{livello}</span>
              <span className="competence-detail__level-label">{label}</span>
              {isOwned && (
                <span
                  className={[
                    'competence-detail__level-owned-badge',
                    ownedIsValid ? '' : 'competence-detail__level-owned-badge--invalid',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={
                    ownedIsValid
                      ? 'Il tuo livello attuale'
                      : 'Il tuo livello attuale non è previsto per questa competenza'
                  }
                  title={
                    ownedIsValid
                      ? 'Il tuo livello attuale'
                      : 'Il tuo livello attuale non è previsto per questa competenza'
                  }
                >
                  <Icon name={ownedIsValid ? 'check' : 'close'} size={14} aria-hidden="true" />
                </span>
              )}
            </div>
            {text === null ? (
              <p className="competence-detail__level-text competence-detail__level-text--empty">
                Livello non previsto per questa competenza
              </p>
            ) : (
              <p className="competence-detail__level-text">{text}</p>
            )}
          </Card>
          );
        })}
      </div>
    </>
  );
}

export default CompetenceInfoPanel;
